/*
 * Games library: fetches `game` metaobjects via the Storefront API and
 * renders a client-side filterable grid. No dependencies.
 */
class GamesLibrary extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    this.tableWrap = this.querySelector('[data-table-wrap]');
    this.tableBody = this.querySelector('[data-table-body]');
    this.status = this.querySelector('[data-status]');
    this.count = this.querySelector('[data-count]');
    this.form = this.querySelector('[data-filters]');
    this.tagsFieldset = this.querySelector('[data-tags]');
    this.tagList = this.querySelector('[data-tag-list]');
    this.viewButtons = [...this.querySelectorAll('[data-view-button]')];
    this.sortButtons = [...this.querySelectorAll('[data-sort-key]')];
    this.games = [];
    this.loaded = false;
    // Staff flagging: only wired when the section rendered the staff data
    // attributes (logged-in customer tagged `staff` + webhook configured).
    this.staff = this.dataset.staff === 'true';
    this.flagUrl = this.dataset.flagUrl || '';
    this.flagSecret = this.dataset.flagSecret || '';
    this.locationUrl = this.dataset.locationUrl || '';
    this.locationSecret = this.dataset.locationSecret || '';
    this.addUrl = this.dataset.addUrl || '';
    const addButton = this.closest('.games-library')?.querySelector('[data-add-game]');
    if (addButton && this.addUrl) addButton.addEventListener('click', () => this.openAddDialog());
    // Map of "bggId|titleLower" -> current flag, populated from the webhook.
    this.flags = {};
    this.view = this.dataset.defaultView === 'cards' ? 'cards' : 'list';
    // Column sort for the list view; the cards view keeps the dropdown sort.
    this.listSort = { key: 'title', dir: 1 };

    this.tagCount = this.querySelector('[data-tag-count]');

    this.form.addEventListener('input', () => { this.showTagCount(); this.render(); });
    this.form.addEventListener('submit', (event) => event.preventDefault());
    this.form.addEventListener('reset', () => {
      // Let the reset apply before re-rendering.
      requestAnimationFrame(() => { this.showTagCount(); this.render(); });
    });

    this.viewButtons.forEach((button) =>
      button.addEventListener('click', () => {
        this.view = button.dataset.viewButton;
        this.render();
      })
    );

    this.sortButtons.forEach((button) =>
      button.addEventListener('click', () => {
        const key = button.dataset.sortKey;
        // THE COLUMN SORTED BEFORE STAYS AS THE ORDER WITHIN TIES (Jonathan, 23/10/2026):
        // sort by Location, then by Flag, and each flag's games stay in location order.
        const same = this.listSort.key === key;
        this.listSort = {
          key,
          dir: same ? -this.listSort.dir : 1,
          then: same ? this.listSort.then : { key: this.listSort.key, dir: this.listSort.dir },
        };
        this.render();
      })
    );

    this.load().catch((error) => {
      this.status.textContent = 'Sorry, the games library failed to load. Please refresh the page to try again.';
      console.error('games-library:', error);
    });
  }

  async load() {
    let cursor = null;
    do {
      const page = await this.fetchPage(cursor);
      this.games.push(...page.games);
      cursor = page.cursor;
    } while (cursor);

    this.games.sort((a, b) => a.title.localeCompare(b.title));
    this.populateTags();
    if (this.staff) await this.loadFlags();
    this.loaded = true;
    this.status.hidden = true;
    this.count.hidden = false;
    this.render();
  }

  async fetchPage(cursor) {
    const query = `
      query Games($cursor: String) {
        metaobjects(type: "game", first: 250, after: $cursor) {
          edges { node { handle fields { key value } } }
          pageInfo { hasNextPage endCursor }
        }
      }`;
    const response = await fetch(
      `https://${this.dataset.shopDomain}/api/${this.dataset.apiVersion}/graphql.json`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.dataset.storefrontToken,
        },
        body: JSON.stringify({ query, variables: { cursor } }),
      }
    );
    if (!response.ok) throw new Error(`Storefront API ${response.status}`);
    const json = await response.json();
    if (json.errors) throw new Error(json.errors.map((e) => e.message).join('; '));

    const connection = json.data.metaobjects;
    return {
      games: connection.edges.map((edge) => this.parseGame(edge.node)),
      cursor: connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null,
    };
  }

  parseGame(node) {
    const fields = {};
    node.fields.forEach((field) => (fields[field.key] = field.value));
    const list = (value) => {
      if (!value) return [];
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };
    const num = (value) => {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : null;
    };
    return {
      handle: node.handle,
      title: fields.title || '',
      bggId: fields.bgg_id ? String(fields.bgg_id).trim() : '',
      minPlayers: num(fields.min_players),
      maxPlayers: num(fields.max_players),
      playtime: num(fields.playtime_minutes),
      complexity: num(fields.complexity),
      rating: num(fields.bgg_rating),
      categories: list(fields.categories),
      year: num(fields.year),
      imageUrl: fields.image_url || '',
      location: fields.location || '',
    };
  }

  // THE TAGS FOLD AWAY (Jonathan, 23/10/2026), so the count of chosen tags is shown on the
  // folded heading - a filter nobody can see must still say it is on.
  showTagCount() {
    if (!this.tagCount) return;
    const chosen = this.tagList.querySelectorAll('input:checked').length;
    this.tagCount.textContent = chosen ? `(${chosen} chosen)` : '';
  }

  populateTags() {
    const tags = new Set();
    this.games.forEach((game) => game.categories.forEach((c) => tags.add(c)));
    [...tags]
      .sort((a, b) => a.localeCompare(b))
      .forEach((tag) => {
        const label = document.createElement('label');
        label.className = 'games-library__tag';
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = 'tag';
        input.value = tag;
        const text = document.createElement('span');
        text.textContent = tag;
        label.append(input, text);
        this.tagList.append(label);
      });
    this.tagsFieldset.hidden = tags.size === 0;
  }

  // Identity shared with the importer and the flag webhook: BGG ID + title.
  flagKey(game) {
    return `${game.bggId}|${game.title.trim().toLowerCase()}`;
  }

  async loadFlags() {
    try {
      const response = await fetch(this.flagUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operation: 'list', secret: this.flagSecret }),
      });
      const json = await response.json();
      if (json.ok && Array.isArray(json.games)) {
        json.games.forEach((g) => {
          this.flags[`${g.bggId}|${String(g.title).trim().toLowerCase()}`] = g.flag;
        });
      }
    } catch (error) {
      console.error('games-library: could not load flags', error);
    }
  }

  buildFlagSelect(game) {
    const select = document.createElement('select');
    select.className = 'games-library__flag';
    const current = this.flags[this.flagKey(game)] || '';
    [
      ['', '—'],
      ['Replace', 'Replace'],
      ['Label', 'Label'],
      ['Missing', 'Missing'],
    ].forEach(([value, label]) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      if (value === current) option.selected = true;
      select.append(option);
    });
    select.dataset.flagged = String(Boolean(current));
    select.addEventListener('change', () => this.setFlag(game, select));
    return select;
  }

  async setFlag(game, select) {
    const key = this.flagKey(game);
    const previous = this.flags[key] || '';
    const value = select.value;
    this.flags[key] = value;
    select.dataset.flagged = String(Boolean(value));
    select.disabled = true;
    try {
      const response = await fetch(this.flagUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'set',
          secret: this.flagSecret,
          bggId: game.bggId,
          title: game.title,
          flag: value,
        }),
      });
      const json = await response.json();
      if (!response.ok || !json.ok) throw new Error(json.error || `HTTP ${response.status}`);
    } catch (error) {
      this.flags[key] = previous;
      select.value = previous;
      select.dataset.flagged = String(Boolean(previous));
      console.error('games-library: could not save flag', error);
      alert('Could not save the flag. Please try again.');
    } finally {
      select.disabled = false;
    }
  }

  // MOVING A GAME (Jonathan, 23/10/2026): supervisors and managers only - the page gets the
  // URL only for them. The backend refuses a location no game is on yet, so a typo cannot
  // invent a shelf; the list of known shelves is offered as suggestions. A game on two
  // shelves stays plain text, because the page cannot say which copy moved.
  locationList() {
    if (this.locationListEl) return this.locationListEl.id;
    const list = document.createElement('datalist');
    list.id = `GamesLocations-${Math.random().toString(36).slice(2)}`;
    const known = new Set(this.games.map((g) => g.location).filter((l) => l && !l.includes(',')));
    [...known].sort((a, b) => a.localeCompare(b)).forEach((loc) => {
      const option = document.createElement('option');
      option.value = loc;
      list.append(option);
    });
    this.append(list);
    this.locationListEl = list;
    return list.id;
  }

  buildLocationInput(game) {
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'games-library__location-input';
    input.value = game.location;
    input.setAttribute('list', this.locationList());
    input.setAttribute('aria-label', `Location of ${game.title}`);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') input.blur();
      if (event.key === 'Escape') {
        input.value = game.location;
        input.blur();
      }
    });
    input.addEventListener('change', () => this.setLocation(game, input));
    return input;
  }

  async setLocation(game, input) {
    const wanted = input.value.trim();
    if (!wanted || wanted.toLowerCase() === game.location.toLowerCase()) {
      input.value = game.location;
      return;
    }
    input.disabled = true;
    try {
      const response = await fetch(this.locationUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: this.locationSecret, bggId: game.bggId, title: game.title, location: wanted }),
      });
      const json = await response.json();
      if (!response.ok || !json.ok) throw new Error(json.error || `HTTP ${response.status}`);
      game.location = json.location;
      input.value = json.location;
      input.dataset.saved = 'true';
      setTimeout(() => delete input.dataset.saved, 1500);
    } catch (error) {
      input.value = game.location;
      console.error('games-library: could not move game', error);
      alert(error.message && !/^HTTP|fetch|JSON/i.test(error.message)
        ? error.message
        : 'Could not save the location. Please try again.');
    } finally {
      input.disabled = false;
    }
  }

  // ADDING A GAME (Jonathan, 28/10/2026): supervisors and managers search BGG, pick the game by
  // its picture, are told which shelf it belongs on, check the shelf and confirm or type a
  // nearby one. The backend decides everything it writes from BGG and the library sheet; the
  // page only names the game, the shelf and the display name.
  async addCall(body) {
    const response = await fetch(this.addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secret: this.locationSecret, ...body }),
    });
    let json = null;
    try { json = await response.json(); } catch { json = null; }
    if (!response.ok || !json) throw new Error('Something went wrong. Please try again.');
    if (!json.ok) throw new Error(json.error || 'Something went wrong. Please try again.');
    return json;
  }

  openAddDialog() {
    if (!this.addDialog) this.buildAddDialog();
    this.showSearchStep();
    this.addDialog.showModal();
    this.addEls.query.focus();
  }

  buildAddDialog() {
    const dialog = document.createElement('dialog');
    dialog.className = 'games-library__dialog';
    dialog.innerHTML = `
      <button type="button" class="games-library__dialog-close" data-close aria-label="Close">&times;</button>
      <div data-step="search">
        <h3 class="games-library__dialog-title">Add a game</h3>
        <form class="games-library__dialog-search" data-search-form>
          <input type="search" class="games-library__input" data-query placeholder="Game name" autocomplete="off" aria-label="Game name">
          <button type="submit" class="button button--secondary" data-search>Search</button>
        </form>
        <button type="button" class="button games-library__dialog-add" data-pick disabled>Add</button>
        <p class="games-library__dialog-message" data-message hidden></p>
        <ul class="games-library__results" data-results></ul>
      </div>
      <div data-step="shelf" hidden>
        <h3 class="games-library__dialog-title" data-chosen-name></h3>
        <div class="games-library__dialog-chosen">
          <img data-chosen-image alt="" hidden>
          <div>
            <p class="games-library__dialog-copies" data-copies hidden></p>
            <label class="games-library__dialog-field">
              <span class="games-library__label">Display name</span>
              <input type="text" class="games-library__input" data-display-name>
            </label>
            <label class="games-library__dialog-field">
              <span class="games-library__label">Suggested shelf</span>
              <span class="games-library__dialog-shelf-row">
                <input type="text" class="games-library__input games-library__dialog-shelf" data-shelf autocomplete="off">
                <button type="button" class="button button--secondary" data-recheck>Recheck</button>
              </span>
            </label>
            <p class="games-library__dialog-why" data-why></p>
          </div>
        </div>
        <p class="games-library__dialog-message" data-shelf-message hidden></p>
        <div class="games-library__dialog-actions">
          <button type="button" class="button button--secondary" data-back>Back</button>
          <button type="button" class="button" data-confirm>Confirm</button>
        </div>
      </div>`;
    this.append(dialog);
    const q = (sel) => dialog.querySelector(sel);
    this.addEls = {
      searchStep: q('[data-step="search"]'), shelfStep: q('[data-step="shelf"]'),
      form: q('[data-search-form]'), query: q('[data-query]'), searchButton: q('[data-search]'),
      pick: q('[data-pick]'), message: q('[data-message]'), results: q('[data-results]'),
      name: q('[data-chosen-name]'), image: q('[data-chosen-image]'), copies: q('[data-copies]'),
      displayName: q('[data-display-name]'), shelf: q('[data-shelf]'), why: q('[data-why]'),
      shelfMessage: q('[data-shelf-message]'), back: q('[data-back]'), confirm: q('[data-confirm]'),
      recheck: q('[data-recheck]'),
    };
    q('[data-close]').addEventListener('click', () => dialog.close());
    this.addEls.form.addEventListener('submit', (event) => { event.preventDefault(); this.searchBgg(); });
    this.addEls.pick.addEventListener('click', () => this.planAdd());
    this.addEls.back.addEventListener('click', () => this.showSearchStep());
    this.addEls.confirm.addEventListener('click', () => this.confirmAdd());
    this.addEls.recheck.addEventListener('click', () => this.recheckShelf());
    this.addEls.shelf.addEventListener('keydown', (event) => { if (event.key === 'Enter') this.confirmAdd(); });
    this.addDialog = dialog;
  }

  addMessage(el, text) {
    el.textContent = text || '';
    el.hidden = !text;
  }

  showSearchStep() {
    this.addEls.searchStep.hidden = false;
    this.addEls.shelfStep.hidden = true;
    this.addMessage(this.addEls.shelfMessage, '');
  }

  async searchBgg() {
    const query = this.addEls.query.value.trim();
    if (query.length < 2) return;
    const els = this.addEls;
    els.searchButton.disabled = true;
    els.pick.disabled = true;
    this.addChoice = null;
    els.results.replaceChildren();
    this.addMessage(els.message, 'Searching BoardGameGeek…');
    try {
      const json = await this.addCall({ op: 'search', query });
      const results = json.results || [];
      this.addMessage(els.message, results.length ? '' : 'BoardGameGeek has nothing by that name. Try fewer words.');
      results.forEach((result) => els.results.append(this.buildResult(result)));
    } catch (error) {
      this.addMessage(els.message, error.message);
    } finally {
      els.searchButton.disabled = false;
    }
  }

  buildResult(result) {
    const li = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'games-library__result';
    button.setAttribute('aria-pressed', 'false');
    const img = document.createElement('img');
    img.alt = '';
    img.loading = 'lazy';
    if (result.thumbnail) img.src = result.thumbnail;
    const text = document.createElement('span');
    text.className = 'games-library__result-text';
    const name = document.createElement('strong');
    name.textContent = result.name;
    const meta = document.createElement('span');
    meta.className = 'games-library__result-meta';
    meta.textContent = [result.year, result.expansion ? 'Expansion' : '', `${result.numRatings.toLocaleString()} ratings`]
      .filter(Boolean).join(' · ');
    text.append(name, meta);
    button.append(img, text);
    button.addEventListener('click', () => {
      this.addEls.results.querySelectorAll('[aria-pressed="true"]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      button.setAttribute('aria-pressed', 'true');
      this.addChoice = result;
      this.addEls.pick.disabled = false;
    });
    button.addEventListener('dblclick', () => this.planAdd());
    li.append(button);
    return li;
  }

  async planAdd() {
    const choice = this.addChoice;
    if (!choice) return;
    const els = this.addEls;
    els.pick.disabled = true;
    this.addMessage(els.message, 'Working out where it goes…');
    try {
      const plan = await this.addCall({ op: 'plan', bggId: choice.bggId });
      this.addPlan = plan;
      els.name.textContent = plan.name + (plan.year ? ` (${plan.year})` : '');
      els.image.hidden = !choice.thumbnail;
      if (choice.thumbnail) els.image.src = choice.thumbnail;
      const copies = plan.copies || [];
      this.addMessage(els.copies, copies.length
        ? 'Already in the library: ' + copies.map((c) => `${c.location} (${c.stock} ${c.stock === 1 ? 'copy' : 'copies'})`).join(', ')
          + '. Confirming on the same shelf adds another copy.'
        : '');
      els.displayName.value = plan.displayName;
      els.shelf.value = plan.suggested || '';
      this.addMessage(els.why, plan.suggested ? '' : 'No shelf could be suggested - type the shelf it is going on.');
      this.addMessage(els.message, '');
      els.searchStep.hidden = true;
      els.shelfStep.hidden = false;
      els.confirm.disabled = false;
      els.shelf.focus();
      els.shelf.select();
    } catch (error) {
      this.addMessage(els.message, error.message);
    } finally {
      els.pick.disabled = !this.addChoice;
    }
  }

  // RECHECK (Jonathan, 28/10/2026): type a section letter (C for co-op) and the suggestion is
  // redone inside that section. A full shelf code works too - its letter is the section.
  async recheckShelf() {
    const els = this.addEls;
    const section = (els.shelf.value.trim().toUpperCase().match(/^(18|[A-Z])/) || [])[1];
    if (!section) {
      this.addMessage(els.shelfMessage, 'Type a section letter, such as C, then Recheck.');
      return;
    }
    els.recheck.disabled = true;
    els.confirm.disabled = true;
    this.addMessage(els.shelfMessage, 'Rechecking…');
    try {
      const plan = await this.addCall({ op: 'plan', bggId: this.addPlan.bggId, section });
      els.shelf.value = plan.suggested;
      this.addMessage(els.shelfMessage, '');
      els.shelf.focus();
      els.shelf.select();
    } catch (error) {
      this.addMessage(els.shelfMessage, error.message);
    } finally {
      els.recheck.disabled = false;
      els.confirm.disabled = false;
    }
  }

  async confirmAdd() {
    const els = this.addEls;
    const location = els.shelf.value.trim();
    if (!location || els.confirm.disabled) return;
    els.confirm.disabled = true;
    els.back.disabled = true;
    this.addMessage(els.shelfMessage, 'Adding…');
    try {
      const json = await this.addCall({
        op: 'add', bggId: this.addPlan.bggId, location, displayName: els.displayName.value.trim(),
      });
      if (json.game) {
        const game = this.parseGame(json.game);
        const at = this.games.findIndex((g) => g.handle === game.handle);
        if (at >= 0) this.games[at] = game; else this.games.push(game);
        // The backend flags every added game "Label"; show it without waiting for a reload.
        this.flags[this.flagKey(game)] = 'Label';
        this.games.sort((a, b) => a.title.localeCompare(b.title));
        this.render();
      }
      const done = json.anotherCopy
        ? `Added another copy on ${json.location} (${json.stock} now).`
        : `Added to ${json.location}.`;
      els.query.value = '';
      els.results.replaceChildren();
      this.addChoice = null;
      els.pick.disabled = true;
      this.showSearchStep();
      this.addMessage(els.message, json.warning ? `${done} ${json.warning}` : done);
    } catch (error) {
      this.addMessage(els.shelfMessage, error.message);
    } finally {
      els.confirm.disabled = false;
      els.back.disabled = false;
    }
  }

  filters() {
    const data = new FormData(this.form);
    return {
      q: (data.get('q') || '').trim().toLowerCase(),
      players: parseInt(data.get('players'), 10) || null,
      maxtime: parseInt(data.get('maxtime'), 10) || null,
      complexity: data.get('complexity') || '',
      tags: data.getAll('tag'),
      sort: data.get('sort') || 'title',
    };
  }

  matches(game, f) {
    if (f.q && !game.title.toLowerCase().includes(f.q)) return false;
    if (f.players) {
      const min = game.minPlayers ?? 1;
      const max = game.maxPlayers ?? min;
      // "7+" filter means seats at least 7.
      if (f.players >= 7 ? max < 7 : min > f.players || max < f.players) return false;
    }
    if (f.maxtime) {
      if (game.playtime === null) return false;
      // The "over 2 hours" option is encoded as 121.
      if (f.maxtime === 121 ? game.playtime <= 120 : game.playtime > f.maxtime) return false;
    }
    if (f.complexity) {
      if (this.band(game) !== f.complexity) return false;
    }
    // Tags combine as AND: the game must carry every selected tag.
    if (f.tags.length && !f.tags.every((tag) => game.categories.includes(tag))) return false;
    return true;
  }

  // BGG weight banded into the same terms the Difficulty filter uses.
  band(game) {
    if (game.complexity === null) return null;
    return game.complexity < 2 ? 'light' : game.complexity <= 3 ? 'medium' : 'heavy';
  }

  playersLabel(game) {
    if (game.minPlayers === null) return '';
    if (game.maxPlayers && game.maxPlayers !== game.minPlayers) {
      return `${game.minPlayers}–${game.maxPlayers}`;
    }
    return `${game.minPlayers}${game.maxPlayers ? '' : '+'}`;
  }

  render() {
    const f = this.filters();
    const shown = this.games.filter((game) => this.matches(game, f));

    if (this.view === 'list') {
      this.sortForList(shown);
    } else if (f.sort === 'rating') {
      shown.sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1));
    } else if (f.sort === 'time') {
      shown.sort((a, b) => (a.playtime ?? Infinity) - (b.playtime ?? Infinity));
    } else {
      shown.sort((a, b) => a.title.localeCompare(b.title));
    }

    // NO COUNT UNTIL SOMETHING IS FILTERED (Jonathan, 23/10/2026: "913 games in the library"
    // was not wanted). A filtered list still says how much of the library it is showing.
    const filtered = shown.length !== this.games.length;
    this.count.hidden = !filtered;
    this.count.textContent = filtered ? `Showing ${shown.length} of ${this.games.length} games` : '';

    this.classList.toggle('games-library--list-view', this.view === 'list');
    this.viewButtons.forEach((button) =>
      button.setAttribute('aria-pressed', String(button.dataset.viewButton === this.view))
    );
    this.sortButtons.forEach((button) => {
      const active = this.view === 'list' && this.listSort.key === button.dataset.sortKey;
      button
        .closest('th')
        .setAttribute('aria-sort', active ? (this.listSort.dir === 1 ? 'ascending' : 'descending') : 'none');
    });

    if (this.loaded) {
      this.grid.hidden = this.view !== 'cards';
      this.tableWrap.hidden = this.view !== 'list';
    }

    if (this.view === 'list') {
      this.tableBody.replaceChildren(...shown.map((game) => this.renderRow(game)));
    } else {
      this.grid.replaceChildren(...shown.map((game) => this.renderCard(game)));
    }
  }

  sortForList(shown) {
    const { key, dir, then } = this.listSort;
    const value = (game, k = key) =>
      ({
        location: game.location || null,
        title: game.title,
        players: game.minPlayers,
        time: game.playtime,
        difficulty: game.complexity,
        rating: game.rating,
        // Staff only (the column exists only for them). Unflagged games sink, like blanks.
        flag: this.flags[this.flagKey(game)] || null,
      })[k];
    // Blanks sink to the bottom whichever way a column is sorted; 0 means a tie.
    const compare = (a, b, k, d) => {
      const va = value(a, k);
      const vb = value(b, k);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      return (typeof va === 'string' ? va.localeCompare(vb) : va - vb) * d;
    };
    shown.sort((a, b) => compare(a, b, key, dir)
      // Ties keep the column sorted before (location within a flag), then fall back to the name.
      || (then && then.key !== key ? compare(a, b, then.key, then.dir) : 0)
      || a.title.localeCompare(b.title));
  }

  renderRow(game) {
    const tr = document.createElement('tr');
    const cell = (text) => {
      const td = document.createElement('td');
      td.textContent = text;
      tr.append(td);
      return td;
    };
    if (this.locationUrl && !game.location.includes(',')) {
      const td = document.createElement('td');
      td.append(this.buildLocationInput(game));
      tr.append(td);
    } else {
      cell(game.location);
    }
    cell(game.title + (game.year ? ` (${game.year})` : '')).className = 'games-library__table-name';
    cell(this.playersLabel(game));
    cell(game.playtime !== null ? String(game.playtime) : '');
    const band = this.band(game);
    cell(band ? band[0].toUpperCase() + band.slice(1) : '');
    cell(game.rating !== null ? game.rating.toFixed(1) : '-');
    if (this.staff) {
      const td = document.createElement('td');
      td.className = 'games-library__flag-cell';
      td.append(this.buildFlagSelect(game));
      tr.append(td);
    }
    return tr;
  }

  renderCard(game) {
    const li = document.createElement('li');
    li.className = 'games-library__card';

    if (game.imageUrl) {
      const img = document.createElement('img');
      img.className = 'games-library__image';
      img.src = game.imageUrl;
      img.alt = game.title;
      img.loading = 'lazy';
      img.width = 300;
      img.height = 300;
      li.append(img);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'games-library__image games-library__image--placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      li.append(placeholder);
    }

    const body = document.createElement('div');
    body.className = 'games-library__card-body';

    const title = document.createElement('h3');
    title.className = 'games-library__title';
    title.textContent = game.title + (game.year ? ` (${game.year})` : '');
    body.append(title);

    const meta = document.createElement('p');
    meta.className = 'games-library__meta';
    const parts = [];
    if (game.minPlayers !== null) parts.push(`${this.playersLabel(game)} players`);
    if (game.playtime !== null) parts.push(`${game.playtime} min`);
    if (game.rating !== null) parts.push(`BGG ${game.rating.toFixed(1)}`);
    meta.textContent = parts.join(' · ');
    body.append(meta);

    if (game.location) {
      const location = document.createElement('span');
      location.className = 'games-library__location';
      location.textContent = `${game.location.includes(',') ? 'Shelves' : 'Shelf'} ${game.location}`;
      body.append(location);
    }

    if (this.staff) {
      const flag = document.createElement('label');
      flag.className = 'games-library__flag-field';
      const label = document.createElement('span');
      label.textContent = 'Flag';
      flag.append(label, this.buildFlagSelect(game));
      body.append(flag);
    }

    li.append(body);
    return li;
  }
}

customElements.define('games-library', GamesLibrary);
